import { Box, Typography } from "@mui/material";

const PIECE_IMG_BASE = "https://www.chess.com/chess-themes/pieces/neo/150";
const PIECE_IMG: Record<string, string> = {
  Q: `${PIECE_IMG_BASE}/wq.png`,
  R: `${PIECE_IMG_BASE}/wr.png`,
  B: `${PIECE_IMG_BASE}/wb.png`,
  N: `${PIECE_IMG_BASE}/wn.png`,
  P: `${PIECE_IMG_BASE}/wp.png`,
  q: `${PIECE_IMG_BASE}/bq.png`,
  r: `${PIECE_IMG_BASE}/br.png`,
  b: `${PIECE_IMG_BASE}/bb.png`,
  n: `${PIECE_IMG_BASE}/bn.png`,
  p: `${PIECE_IMG_BASE}/bp.png`,
};

const PIECE_VALUE: Record<string, number> = {
  p: 1,
  n: 3,
  b: 3,
  r: 5,
  q: 9,
  P: 1,
  N: 3,
  B: 3,
  R: 5,
  Q: 9,
};

/** Display order: queen, rook, bishop, knight, pawn */
const DISPLAY_ORDER = "qrbnp";

interface CapturedPiecesProps {
  fen: string;
  color?: "w" | "b";
}

/**
 * Shows captured pieces below a chessboard — pieces displayed as small
 * overlapping icons (staggered 5px), grouped by type. Material advantage
 * shown as +N.
 */
export const CapturedPieces = ({ fen, color }: CapturedPiecesProps) => {
  const startingWhite: Record<string, number> = { P: 8, N: 2, B: 2, R: 2, Q: 1 };
  const startingBlack: Record<string, number> = { p: 8, n: 2, b: 2, r: 2, q: 1 };

  const boardPart = fen.split(" ")[0];
  const currentWhite: Record<string, number> = { P: 0, N: 0, B: 0, R: 0, Q: 0 };
  const currentBlack: Record<string, number> = { p: 0, n: 0, b: 0, r: 0, q: 0 };

  for (const ch of boardPart) {
    if (ch in currentWhite) currentWhite[ch]++;
    else if (ch in currentBlack) currentBlack[ch]++;
  }

  // Build captured arrays grouped by piece type
  const capturedWhite: string[] = []; // white pieces captured (by black)
  const capturedBlack: string[] = []; // black pieces captured (by white)

  for (const piece of Object.keys(startingWhite)) {
    const diff = startingWhite[piece] - currentWhite[piece];
    for (let i = 0; i < diff; i++) capturedWhite.push(piece);
  }
  for (const piece of Object.keys(startingBlack)) {
    const diff = startingBlack[piece] - currentBlack[piece];
    for (let i = 0; i < diff; i++) capturedBlack.push(piece);
  }

  // Sort by value (queen first), same piece types stay together for stacking
  const sortPieces = (pieces: string[]) =>
    pieces.sort((a, b) => DISPLAY_ORDER.indexOf(a.toLowerCase()) - DISPLAY_ORDER.indexOf(b.toLowerCase()));
  sortPieces(capturedWhite);
  sortPieces(capturedBlack);

  // Material advantage
  const whiteCapturedValue = capturedBlack.reduce((s, p) => s + PIECE_VALUE[p], 0);
  const blackCapturedValue = capturedWhite.reduce((s, p) => s + PIECE_VALUE[p], 0);
  const advantage = whiteCapturedValue - blackCapturedValue;

  /** Renders a row of staggered piece icons. Same piece types overlap tightly (5px offset), different types have a small gap. */
  const PieceRow = ({ pieces, showAdvantage }: { pieces: string[]; showAdvantage: number }) => {
    const ICON_SIZE = 20;
    const SAME_OFFSET = 5; // tight stack for same piece type (5px displacement)
    const DIFF_GAP = 20; // full icon width gap between different piece types

    // Calculate positions
    let x = 0;
    const items: { piece: string; left: number }[] = [];
    for (let i = 0; i < pieces.length; i++) {
      items.push({ piece: pieces[i], left: x });
      // Next position: tight overlap if same type, gap if different
      if (i < pieces.length - 1) {
        const sameType = pieces[i].toLowerCase() === pieces[i + 1].toLowerCase();
        x += sameType ? SAME_OFFSET : DIFF_GAP;
      }
    }
    const totalWidth = x + ICON_SIZE;

    return (
      <Box sx={{ display: "flex", alignItems: "center", minHeight: (theme) => theme.spacing(3) }}>
        <Box sx={{ position: "relative", width: totalWidth, height: ICON_SIZE }}>
          {items.map((item, i) => (
            <img
              key={i}
              src={PIECE_IMG[item.piece]}
              alt={item.piece}
              style={{
                position: "absolute",
                left: item.left,
                top: 0,
                width: ICON_SIZE,
                height: ICON_SIZE,
                opacity: 0.9,
              }}
            />
          ))}
        </Box>
        {showAdvantage > 0 && (
          <Typography
            sx={{ fontSize: "caption.fontSize", fontWeight: "fontWeightBold", color: "text.primary", ml: 0.5 }}
          >
            +{showAdvantage}
          </Typography>
        )}
      </Box>
    );
  };

  if (color) {
    const playerAdvantage = color === "w" ? advantage : -advantage;
    return (
      <PieceRow pieces={color === "w" ? capturedWhite : capturedBlack} showAdvantage={Math.max(playerAdvantage, 0)} />
    );
  }

  return (
    <Box sx={{ mt: 0.5, px: 0.5 }}>
      <PieceRow pieces={capturedBlack} showAdvantage={Math.max(advantage, 0)} />
      <PieceRow pieces={capturedWhite} showAdvantage={Math.max(-advantage, 0)} />
    </Box>
  );
};
